// Two-pane gen workspace. Left: chat with the LLM (stubbed — the
// conversation loop isn't defined yet). Right: live preview of the dev
// app, gated on `app.run_succeeded`. Until that signal is true, the right
// pane shows a frosted backdrop with the perimetr.platform logo centered —
// the app is mid-flight between deploys and intermediate state must NOT
// be shown to the operator (per spec).
//
// The component is purely presentational: it derives `ready` and `devUrl`
// from the App record alone, and renders. No side effects, no fetches —
// AppDetail already handles polling.
//
// Layout notes:
//   - Fills the viewport from just below the sticky navbar to the bottom
//     (`h-[calc(100vh-3.5rem)]`). AppDetail negates Shell's vertical
//     padding so this calc lines up.
//   - Chat is seamless (no card, no border) — blends with the page bg,
//     reads as a column rather than an inserted card.
//   - A 3D-look vertical divider sits between the two panes (bevel via a
//     dark left edge + light right edge + faint center line + grip
//     stripes in the middle).
import { memo, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ideaFromState } from "../lib/pending_idea";
import ReactMarkdown from "react-markdown";
import remarkGfm     from "remark-gfm";
import type { App, Message, SharedAppView } from "../api";
import { setVisual } from "../api";
import { CHAT_MIN_PX, clampChatWidth, readStoredWidth } from "../lib/chat_width";
import { useChat, type CurrentAction, type AskPending } from "../hooks/useChat";
import { useDictation } from "../hooks/useDictation";
import { useAttachments, type Attachments } from "../hooks/useAttachments";
import AnnotateOverlay from "./AnnotateOverlay";
import { Logo } from "./Logo";
import { ATTACH_TYPES, filesFromPaste } from "../lib/attachment";
import { DOC_TYPES } from "../lib/document";
import { joinDictated } from "../lib/dictation";
import { extractActions, extractAskQuestion, actionLabel, type Action } from "../lib/parse_actions";
import { previewReady } from "../lib/preview_ready";
import { queueMode } from "../lib/operator_inbox";
import { previewUrl } from "../lib/preview_url";
import { PREVIEW_WIDTHS, previewOrigin, widthStyle } from "../lib/preview_frame";
import { docMessage, editModeMessage, readToConsole, reloadMessage } from "../../../frontend-unit-bundle/platform/src/_runtime/visual_bridge";
import { verifierChangement } from "../lib/visual_check";
import { creerMesureur, type Mesureur } from "../lib/visual_guard";
import { isCorrupted } from "../lib/sanitize_narration";
import { groupFeed, summaryLabel, formatDuration, type FeedRow } from "../lib/run_feed";
import { planStripOf, canStart, suggestedPart, type StripPart } from "../lib/plan_strip";
import DataPanel from "./DataPanel";
import FilesPanel from "./FilesPanel";
import SecretsPanel from "./SecretsPanel";
import CustomDomainPanel from "./CustomDomainPanel";
import BrowserKeysPanel from "./BrowserKeysPanel";
import BackupsPanel from "./BackupsPanel";
import type { WorkspaceView } from "./PreviewDataToggle";

// Composer modes. Mirrors the v0/Bolt/Lovable convention:
//   - act:  the LLM makes the change (the server auto-picks build for a new app
//           vs edit for a sealed one — the operator never has to know which).
//   - chat: the LLM just answers questions about the app, no code changes.
// The operator picks this explicitly; the system never guesses chat-vs-act.
type ChatMode = "chat" | "act";

// How to reach the DEV slot in a browser. The SCHEME is not configured — it is the page's
// own, because it can only ever be the page's: an http frame inside an https page is
// blocked as mixed content. See lib/preview_url.ts for the white screen that taught us.
//
// Only the PORT remains a setting, and only a plain-http dev page ever uses it: there the
// interface and the apps listen on different ports, which a page cannot know.
const DEV_APP_PORT = import.meta.env.VITE_DEV_APP_PORT ?? "8080";

export default function Workspace({ app, activeRunId, onRunComplete, view = "preview", attachments = false }: { app: App; activeRunId?: string | null; onRunComplete?: () => void; view?: WorkspaceView; attachments?: boolean }) {
  // COMBIEN DE RUNS SE SONT TERMINÉS, et c'est le signal qui recharge l'aperçu.
  //
  // Le gabarit des apps coupe le rechargement à chaud (`hmr: false`) et son commentaire suppose
  // que « the operator's preview is re-deployed, not hot-patched ». Personne ne tenait cette
  // hypothèse : le cadre reste monté exprès (voir RightPane), donc une modification n'apparaissait
  // jamais sans rafraîchissement manuel — sa plainte du 2026-10-06.
  //
  // À LA FIN DU RUN, PAS À CHAQUE DÉPLOIEMENT : un build déploie des dizaines de fois, et
  // recharger à chacun ferait clignoter l'aperçu pendant tout le travail. Une modification = un
  // run = un rechargement, ce qui est exactement ce qu'il a décrit.
  const [runsFinis, setRunsFinis] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const { chatWidth, resizeChat, resetChat } = useChatWidth(rootRef);
  const ready  = previewReady(app);
  const devUrl = previewUrl(app.dev.ui_hostname, window.location.protocol, DEV_APP_PORT);

  return (
    // h = 100vh − navbar (h-14 = 56px = 3.5rem). On narrow screens (lg
    // breakpoint and below) we collapse to single-column so the chat and
    // preview stack instead of fighting for width.
    <div
      ref={rootRef}
      className="flex h-[calc(100vh-3.5rem)] min-h-[520px] flex-col lg:flex-row"
      // La largeur du fil voyage par une VARIABLE CSS, pas par un style en ligne sur la
      // colonne. Deux raisons :
      //   • le point de rupture reste dans le CSS (`lg:w-[var(--chat-w,…)]`). Un style en
      //     ligne s'appliquerait AUSSI en dessous de 1024 px, où les deux panneaux sont
      //     empilés et où le fil doit prendre toute la largeur ;
      //   • le repli de la variable EST la règle d'avant, donc tant que l'opérateur n'a
      //     pas tiré la poignée, rien ne change — pas même d'un pixel.
      style={chatWidth === null ? undefined : ({ "--chat-w": `${chatWidth}px` } as CSSProperties)}
    >
      {/* Le rappel de l'appelant est TOUJOURS appelé — on compte EN PLUS, on ne remplace pas :
          AppDetail s'en sert pour rafraîchir l'app (le flou de l'aperçu part de là). */}
      <ChatColumn
        slug={app.slug}
        initialActiveRunId={activeRunId ?? null}
        onRunComplete={() => { setRunsFinis((n) => n + 1); onRunComplete?.(); }}
        attachments={attachments}
      />
      <Divider3D onResize={resizeChat} onReset={resetChat} width={chatWidth} />
      <RightPane ready={ready} url={devUrl} slug={app.slug} view={view} runsFinis={runsFinis} />
    </div>
  );
}

/**
 * L'ÉCRAN D'UNE APP PARTAGÉE AVEC MOI — en lecture (platform-service/lib/app_sharing).
 *
 * « Comme la vue actuelle, sans la zone de saisie » (Lemrabott, 2026-10-14) : la discussion — pendant
 * et après le run — et l'aperçu de dev. Rien d'autre n'est MONTÉ : ni zone de saisie, ni bouton
 * Stop, ni bandeau du plan, ni réponses à une question, ni panneau de données. Un composant à part
 * plutôt qu'un drapeau `readOnly` glissé dans la colonne du propriétaire : un drapeau oublié dans une
 * branche y laisserait un bouton ; ici, ce qui n'est pas écrit n'existe pas. Et le serveur refuse de
 * toute façon chaque action à un lecteur.
 *
 * Le fil et son défilement sont CEUX du propriétaire (ThreadView, useThreadScroll) : un seul rendu.
 */
export function SharedWorkspace({ app, runId, sharedBy }: { app: SharedAppView; runId: string | null; sharedBy: string | null }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const { chatWidth, resizeChat, resetChat } = useChatWidth(rootRef);
  const devUrl = previewUrl(app.dev.ui_hostname, window.location.protocol, DEV_APP_PORT);
  return (
    <div
      ref={rootRef}
      className="flex h-[calc(100vh-3.5rem)] min-h-[520px] flex-col lg:flex-row"
      style={chatWidth === null ? undefined : ({ "--chat-w": `${chatWidth}px` } as CSSProperties)}
    >
      <ReadOnlyChatColumn slug={app.slug} runId={runId} sharedBy={sharedBy} />
      <Divider3D onResize={resizeChat} onReset={resetChat} width={chatWidth} />
      <PreviewColumn ready={!!app.run_succeeded} url={devUrl} />
    </div>
  );
}

function ReadOnlyChatColumn({ slug, runId, sharedBy }: { slug: string; runId: string | null; sharedBy: string | null }) {
  // `runId` change quand le propriétaire lance un nouveau run (lib/shared_watch) : useChat relit
  // alors le fil et rejoint le flux — son chemin de reprise, inchangé.
  const chat = useChat(slug, { initialActiveRunId: runId });
  const scroll = useThreadScroll(chat.messages.length);
  return (
    <div className="relative flex h-full w-full flex-shrink-0 flex-col overflow-hidden lg:w-[var(--chat-w,clamp(380px,22vw,520px))]">
      <ThreadView chat={chat} scroll={scroll} />
      <div className="border-t border-border px-4 py-3 text-center text-[11px] text-text-secondary">
        {chat.runActive ? "Building now — you are watching live. " : ""}
        Read-only{sharedBy ? <> — shared with you by <span className="font-medium text-text">{sharedBy}</span></> : null}.
      </div>
    </div>
  );
}

/**
 * Le bouton « Edit look » est-il offert à l'opérateur ?
 *
 * NON, décidé le 2026-10-07 : « we will just proceed by chat input instead of the mouse editing
 * for now ». Une app se retouche en le DEMANDANT dans le fil, pas en traînant une photo à la
 * souris. Deux façons de faire la même chose, dont une inachevée, est une façon de trop.
 *
 * UN INTERRUPTEUR, ET PAS UNE SUPPRESSION. « Pour l'instant » : les onze pièces de l'éditeur
 * restent là, éprouvées, et une seule ligne les rallume. Les arracher coûterait un jour de
 * travail à les remettre, et le code arraché est celui qui revient faux.
 *
 * CE QUE CACHER LE BOUTON ÉTEINT, et c'est pourquoi c'est suffisant : `setModifier` n'est appelé
 * de NULLE PART ailleurs (vérifié), donc le mode ne peut plus s'allumer. Le cadre de mesure
 * n'existe que pendant ce mode, l'app ne reçoit donc jamais `editMode(true)`, et aucune retouche
 * ne peut être enregistrée. Rien ne tourne à vide.
 *
 * Ce que ça ne touche PAS : le rechargement de l'aperçu à la fin d'un run, et `visual.json`, que
 * l'app importe toujours — un `import` qui ne résout pas est un build qui échoue
 * (frontend-unit-bundle/lib/app_root_files).
 */
const EDIT_LOOK_SHOWN: boolean = false;   // `boolean` et non le littéral : c'est un interrupteur, pas une constante morte.

/** Où le réglage de l'opérateur est retenu. Propre à ce navigateur, comme une préférence
 *  d'affichage doit l'être : ce n'est pas une donnée de l'app, et elle n'a rien à faire
 *  sur le serveur. */
const CHAT_WIDTH_KEY = "perimetr.chatWidth";

/**
 * La largeur du fil réglée par l'opérateur, et les deux gestes qui la changent.
 *
 * `null` = il n'y a jamais touché, et la colonne garde sa règle CSS d'origine. C'est ce
 * qui rend l'ajout invisible tant qu'on ne s'en sert pas.
 *
 * Le stockage du navigateur est enveloppé dans des try/catch : en navigation privée ou
 * avec les données de site bloquées, une lecture peut lever. Une préférence d'affichage
 * ne doit jamais empêcher la console de s'ouvrir.
 */
function useChatWidth(rootRef: React.RefObject<HTMLDivElement | null>) {
  const [chatWidth, setChatWidth] = useState<number | null>(null);

  useEffect(() => {
    let brut: string | null = null;
    try { brut = window.localStorage.getItem(CHAT_WIDTH_KEY); } catch { /* stockage bloqué */ }
    const lu = readStoredWidth(brut);
    if (lu !== null) setChatWidth(clampChatWidth(lu, window.innerWidth));
  }, []);

  // Le glissement donne une position de pointeur ; la largeur est cette position MOINS le
  // bord gauche du conteneur, parce que la colonne ne commence pas forcément à 0 (barres
  // latérales du navigateur, marges). Mesuré à chaque mouvement : une valeur gardée au
  // premier appui serait fausse dès que la fenêtre bouge pendant le glissement.
  const resizeChat = useCallback((clientX: number, persiste: boolean) => {
    const bord = rootRef.current?.getBoundingClientRect().left ?? 0;
    const borne = clampChatWidth(clientX - bord, window.innerWidth);
    setChatWidth(borne);
    if (persiste) { try { window.localStorage.setItem(CHAT_WIDTH_KEY, String(borne)); } catch { /* stockage bloqué */ } }
  }, [rootRef]);

  // Le double-clic rend la disposition d'origine. Un réglage qu'on ne peut pas défaire est
  // un piège : l'opérateur a tiré trop loin et n'a plus de repère pour revenir.
  const resetChat = useCallback(() => {
    setChatWidth(null);
    try { window.localStorage.removeItem(CHAT_WIDTH_KEY); } catch { /* stockage bloqué */ }
  }, []);

  return { chatWidth, resizeChat, resetChat };
}

// Right pane: the live preview, or (once the app has built) the tenant Data
// view. Which one shows is driven by the navbar Preview|Data toggle — the
// `view` prop is lifted to AppDetail. No toggle bar here, so the content uses
// the FULL pane height. The preview iframe stays MOUNTED (hidden, not torn
// down) so switching to Data and back doesn't reload the running app.
// The panels no longer take a settings callback: there is ONE gear, on the nav bar beside the view
// switch (routes/AppDetail). Three gears in three panel bands were three buttons doing one thing, and
// each was a reason for its panel to carry a band at all.
function RightPane({ ready, url, slug, view, runsFinis }: { ready: boolean; url: string; slug: string; view: WorkspaceView; runsFinis: number }) {
  // MAXIMISED FILLS THE BROWSER WINDOW — not the screen.
  //
  // The first version called the native Fullscreen API, which takes over the whole display and hides
  // the browser itself. The operator's correction: "not full screen of the whole screen just within the
  // browser". They are judging an app they will look at in a browser, so the useful largest view is the
  // window, with the tab bar and the address bar still there.
  //
  // Done by moving THIS pane to `fixed inset-0` rather than by hiding the chat column and the nav bar
  // from above. Two reasons: the state stays in the one component it concerns (no new prop threaded
  // through AppDetail), and — the load-bearing one — it is the SAME element with different classes, so
  // the iframe is never unmounted. Hiding siblings instead would have worked too; re-parenting the
  // iframe would not, because a remounted iframe RELOADS the app, losing whatever the operator had
  // navigated to or typed into it.
  const [maxed, setMaxed] = useState(false);
  /** La largeur de l'aperçu : téléphone, tablette ou bureau. « Tu bascules et tu vois » —
   *  c'est ce qui rend VISIBLE la promesse de responsivité au moment du choix. */
  const [largeur, setLargeur] = useState<number | null>(null);
  /** Le mode « modifier ». Un mode qu'on ALLUME, sinon on déplace un bouton en voulant le
   *  cliquer — le piège classique d'un éditeur en place. */
  const [modifier, setModifier] = useState(false);
  /** L'aperçu porte-t-il l'éditeur ? Il l'annonce lui-même (`ready`). Sans ça la console
   *  offrirait un bouton qui ne fait rien sur toute app déployée avant cette version. */
  const [editable, setEditable] = useState(false);
  const cadre = useRef<HTMLIFrameElement>(null);
  const origine = previewOrigin(url);
  /** CE QUE LA RETOUCHE AURAIT CASSÉ, quand elle a été refusée. `null` au repos : un bandeau
   *  permanent sur l'app du client serait du bruit. */
  const [refus, setRefus] = useState<string | null>(null);
  /** LE CADRE QUI MESURE, et il est à part du cadre qu'on regarde.
   *
   *  Mesurer trois largeurs avant et après dans l'aperçu VISIBLE le ferait se tordre pendant une
   *  seconde à chaque retouche. Celui-ci vit hors de l'écran : il coûte un chargement de plus de
   *  l'app, et rien de visible. Il n'existe que pendant le mode modification — charger une
   *  seconde copie de l'app du client pour rien serait une dépense sans contrepartie. */
  const mesure = useRef<HTMLIFrameElement>(null);
  const mesureur = useRef<Mesureur | null>(null);

  /**
   * MESURER, PUIS ENREGISTRER — OU REFUSER ET REMETTRE.
   *
   * Sa contrainte, le 2026-10-05 : « on veut aussi que ca ne nique pas la responsivité ». Les
   * gestes ne produisent que des pourcentages, donc ils ne figent aucun pixel — mais une photo à
   * 100 % dans un conteneur qui a déjà des marges sort quand même de l'écran. La promesse ne
   * valant que mesurée, on mesure l'app aux trois largeurs avant et après, hors de l'écran.
   *
   * SANS LES DEUX DOCUMENTS, ON ENREGISTRE SANS VÉRIFIER. C'est le cas de toute app déployée
   * avant cette version : elle n'a pas de quoi les envoyer. Refuser la retouche de l'opérateur
   * parce que son app est ancienne le punirait d'un problème qui n'est pas le sien.
   */
  const enregistrer = useCallback(async (
    name: string,
    patch: Record<string, unknown>,
    docs: { before: unknown; after: unknown } | undefined,
  ) => {
    setRefus(null);
    const m = mesureur.current;
    if (docs !== undefined && m !== null) {
      const v = await verifierChangement(m.mesurer, docs.before, docs.after);
      if (!v.ok) {
        setRefus(v.why);
        // ON REMET L'APERÇU. L'app a déjà appliqué la retouche pour la montrer pendant le
        // geste ; sans ça, l'opérateur verrait un refus ET la photo cassée sous les yeux. Lui
        // renvoyer l'état enregistré remet aussi sa référence de comparaison.
        if (origine !== null) {
          try { cadre.current?.contentWindow?.postMessage(docMessage(docs.before as never), origine); }
          catch { /* le cadre n'est pas prêt : le prochain `doc` le remettra */ }
        }
        return;
      }
    }
    // LE SERVEUR BORNE ET ÉCRIT. On ne garde rien en local : l'aperçu montre déjà le résultat
    // (il l'a appliqué lui-même), et le fichier est la seule vérité.
    await setVisual(slug, name, patch).catch(() => { /* le refus s'affiche côté serveur */ });
  }, [origine, slug]);

  // LE MESUREUR SUIT LE CADRE DE MESURE. Il n'existe que pendant le mode modification, et il est
  // FERMÉ en sortant : une mesure encore en vol rendrait `null` au lieu de retenir son appelant
  // pour toujours.
  useEffect(() => {
    if (!modifier || origine === null) { mesureur.current = null; return; }
    const m = creerMesureur({
      setWidth: (px) => {
        const f = mesure.current;
        if (f === null) return;
        f.style.width = `${px}px`;
        // FORCER LA DISPOSITION DU PARENT : la nouvelle taille du cadre doit être propagée
        // AVANT qu'on demande la mesure, sinon l'app rapporterait l'ancienne largeur et
        // `visual_check` écarterait sa réponse. Une lecture suffit à la provoquer.
        void f.offsetWidth;
      },
      post: (message) => {
        try { mesure.current?.contentWindow?.postMessage(message, origine); }
        catch { /* pas prêt : le délai de garde rendra `null`, donc on acceptera */ }
      },
      delai: (ms, f) => { const t = window.setTimeout(f, ms); return () => window.clearTimeout(t); },
    });
    mesureur.current = m;
    return () => { m.fermer(); mesureur.current = null; };
  }, [modifier, origine]);

  // Le refus disparaît en quittant le mode : il parle d'un geste, pas d'un état de l'app.
  useEffect(() => { if (!modifier) setRefus(null); }, [modifier]);

  // RECHARGER L'APERÇU QUAND UN RUN SE TERMINE.
  //
  // C'est L'APP qui recharge, pas nous : on ne peut pas appeler `location.reload()` dans une
  // iframe d'une AUTRE origine, et réécrire son `src` la ramènerait à l'accueil — ce que ce
  // composant évite depuis toujours en ne la remontant jamais. Le message lui demande de
  // recharger SA page, donc l'opérateur revient là où il était.
  //
  // `runsFinis > 0` saute le premier rendu : au montage il n'y a rien de neuf à montrer, et un
  // rechargement à l'ouverture de l'écran serait du bruit.
  //
  // Une app déployée avant cette version n'a pas l'écouteur : le message tombe dans le vide,
  // sans erreur. Elle gardera donc l'ancien comportement jusqu'à son prochain run — comme le
  // bouton « Edit look ».
  useEffect(() => {
    if (runsFinis === 0 || origine === null) return;
    try { cadre.current?.contentWindow?.postMessage(reloadMessage(), origine); }
    catch { /* le cadre n'est pas prêt : il affiche déjà du frais, il vient de se charger */ }
  }, [runsFinis, origine]);

  // LE PONT AVEC LES DEUX CADRES. La validation est dans _runtime/visual_bridge, côté app comme
  // ici : étiquette, origine EXACTE, et fenêtre d'envoi = celle du cadre. Pas d'origine sûre
  // (`previewOrigin` rend null) ⇒ liste vide ⇒ le pont refuse tout. Fermé par défaut.
  //
  // DEUX CADRES, DEUX CONFIANCES, ET ELLES NE SE MÉLANGENT PAS. Le pont compare la fenêtre
  // d'envoi à CELLE QU'ON ATTEND, et on n'attend pas la même des deux : seul le cadre visible
  // peut annoncer un geste, seul le cadre de mesure peut rendre une mesure. Les confondre
  // laisserait l'app du client répondre à une mesure qu'on ne lui a pas demandée, ou le cadre
  // caché — qui n'est même pas en mode modification — enregistrer une retouche.
  useEffect(() => {
    if (origine === null) return;
    const onMessage = (e: MessageEvent) => {
      const brut = { origin: e.origin, data: e.data, source: e.source };

      const vu = readToConsole(brut, { origins: [origine], source: cadre.current?.contentWindow ?? null });
      if (vu !== null) {
        if (vu.kind === "ready") setEditable(true);
        else if (vu.kind === "commit") void enregistrer(vu.name, vu.patch as Record<string, unknown>, vu.docs);
        return;
      }

      const mes = readToConsole(brut, { origins: [origine], source: mesure.current?.contentWindow ?? null });
      if (mes?.kind === "measured") mesureur.current?.recevoir(mes.id, mes.page);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [origine, enregistrer]);

  // Le mode suit le bouton, dans l'aperçu. Envoyé à l'origine EXACTE, jamais à `*`.
  useEffect(() => {
    if (origine === null) return;
    try { cadre.current?.contentWindow?.postMessage(editModeMessage(modifier), origine); } catch { /* le cadre n'est pas prêt */ }
  }, [modifier, origine]);

  // Escape exits. Not because a `fixed` overlay demands it — nothing is trapped — but because every
  // maximised view a person has ever used exits that way, and the alternative is hunting for the button
  // again in the corner of an app that may have drawn its own thing there.
  useEffect(() => {
    if (!maxed) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setMaxed(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [maxed]);

  // Before the first successful build there's nothing to preview or browse —
  // keep the frosted placeholder (the navbar toggle is hidden then too).
  if (!ready) return <PreviewColumn ready={false} url={url} />;
  return (
    <div className={maxed
      ? "fixed inset-0 z-40 overflow-hidden bg-bg"
      : "relative h-full w-full flex-1 overflow-hidden bg-bg"}>
      <iframe
        ref={cadre}
        src={url}
        title="App preview"
        className={`h-full border-0 bg-white ${largeur === null ? "w-full" : ""} ${view !== "preview" ? "hidden" : ""}`}
        style={widthStyle(largeur)}
        /* STAYS WHITE, deliberately: this is the CUSTOMER'S APP rendering, not our chrome. It is the
           canvas the iframe paints on before the app's own background arrives, and a dark frame behind
           a light app is a worse seam than a brief white one. The app decides its own theme. */
        /* `allow-downloads` : sans ce jeton, un clic sur « Télécharger » dans l'app ne fait RIEN —
           le navigateur refuse le téléchargement et ne le dit ni à l'utilisateur ni à la page.
           Mesuré le 2026-09-29 : le même lien, ouvert hors de l'iframe, rendait le PDF entier
           (200, `attachment`, 2 732 268 octets) ; seul le bac à sable bloquait. MDN : le jeton
           « allows downloading files through an <a> … with the download attribute ». C'est une
           capacité AJOUTÉE, nommée — le bac à sable reste, et un fichier téléversé part déjà avec
           `nosniff` et en pièce jointe pour tout type exécutable (file_serve.ts). */
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-modals allow-downloads"
      />
      {view === "files" && (
        <div className="absolute inset-0 bg-bg">
          <FilesPanel slug={slug} />
        </div>
      )}
      {view === "data" && (
        <div className="absolute inset-0 bg-bg">
          <DataPanel slug={slug} />
        </div>
      )}
      {view === "secrets" && (
        <div className="absolute inset-0 bg-bg">
          <SecretsPanel slug={slug} />
        </div>
      )}
      {view === "keys" && (
        <div className="absolute inset-0 bg-bg">
          {/* The keys the app's UI needs in the BROWSER — a separate panel from Secrets
              because the disclosure rule is the opposite: these ship inside the bundle. */}
          <BrowserKeysPanel slug={slug} />
        </div>
      )}
      {view === "domain" && (
        <div className="absolute inset-0 overflow-auto bg-bg">
          {/* L'adresse de l'app : celle que la plateforme offre, et celle que son propriétaire
              apporte. Un onglet à part de « Preview » parce que ce n'est pas ce que l'app FAIT mais
              où on la trouve — et parce que les deux lignes DNS à copier se lisent posément. */}
          <CustomDomainPanel slug={slug} />
        </div>
      )}
      {view === "backups" && (
        <div className="absolute inset-0 bg-bg">
          <BackupsPanel slug={slug} />
        </div>
      )}

      {/* LE CADRE QUI MESURE. Hors de l'écran, jamais lu par personne.
          `left: -10000px` et pas `display: none` : un cadre non affiché n'a AUCUNE disposition,
          donc toutes les boîtes y mesureraient zéro. Et pas `visibility: hidden` non plus, pour
          ne pas donner au navigateur une raison de plus de brider ses trames d'animation —
          l'app en attend deux avant de mesurer.
          Hauteur fixe et généreuse : une disposition verticale réaliste, sinon tout se replie.
          `aria-hidden` + `tabIndex={-1}` : il ne doit exister pour aucun lecteur d'écran ni
          recevoir le focus, c'est un instrument de mesure et pas un contenu. */}
      {modifier && (
        <iframe
          ref={mesure}
          src={url}
          title="Measuring frame"
          aria-hidden="true"
          tabIndex={-1}
          className="pointer-events-none border-0 bg-white"
          style={{ position: "fixed", left: "-10000px", top: 0, width: "360px", height: "900px" }}
        />
      )}

      {/* LE REFUS. Il ne s'affiche QUE quand une retouche a été écartée, et il dit la largeur et
          l'élément : un refus sans cause n'est pas réparable.
          Au-dessus des commandes, à gauche comme elles, et il flotte sur l'APP DU CLIENT — donc
          autocontrasté et non thémé, comme les commandes. */}
      {view === "preview" && refus !== null && (
        <div
          role="status"
          className="absolute bottom-14 left-3 z-10 max-w-sm rounded-lg bg-black/80 px-3 py-2 text-[11px] leading-relaxed text-white backdrop-blur-sm"
        >
          {refus}
        </div>
      )}

      {/* LES COMMANDES DE L'APERÇU — la largeur, et le mode « modifier ».
          En bas à GAUCHE, là où le bouton « remplir la fenêtre » n'est pas : ils flottent sur
          l'APP DU CLIENT, dont on ne contrôle pas le fond, et deux groupes au même endroit se
          recouvriraient. Autocontrastés et NON thémés, pour la même raison que lui. */}
      {view === "preview" && (
        <div className="absolute bottom-3 left-3 z-10 flex items-center gap-1 rounded-lg bg-black/70 p-1 backdrop-blur-sm">
          {PREVIEW_WIDTHS.map((w) => (
            <button
              key={w.id}
              type="button"
              onClick={() => setLargeur(w.px)}
              aria-label={`Preview at ${w.label.toLowerCase()} width`}
              aria-pressed={largeur === w.px}
              title={w.label}
              /* 28 px de haut : au-dessus du minimum de 24 px de WCAG 2.5.8, celui que
                 l'audit des largeurs applique déjà à toutes les cibles. */
              className={`h-7 rounded-md px-2.5 text-[11px] font-medium transition ${
                largeur === w.px ? "bg-white text-black" : "text-white/70 hover:text-white"}`}
            >
              {w.label}
            </button>
          ))}
          {/* LE BOUTON N'EXISTE QUE SI L'APERÇU PORTE L'ÉDITEUR. Toute app déployée avant cette
              version n'en a pas : un bouton qui ne fait rien ferait conclure à une panne.
              Et il est RANGÉ pour l'instant — voir EDIT_LOOK_SHOWN. */}
          {EDIT_LOOK_SHOWN && editable && (
            <>
              <span className="mx-0.5 h-5 w-px bg-white/25" aria-hidden="true" />
              <button
                type="button"
                onClick={() => setModifier((v) => !v)}
                aria-label={modifier ? "Stop editing the look" : "Edit the look"}
                aria-pressed={modifier}
                title={modifier ? "Done editing" : "Drag a photo to move or resize it"}
                className={`h-7 rounded-md px-2.5 text-[11px] font-medium transition ${
                  modifier ? "bg-white text-black" : "text-white/70 hover:text-white"}`}
              >
                {modifier ? "Done" : "Edit look"}
              </button>
            </>
          )}
        </div>
      )}

      {/* FULL SCREEN — only over the preview.
          The other views are the platform's own panels; giving a file tree the whole screen is not a
          thing anyone wants, and a button that appeared on six tabs to be useful on one is six chances
          to wonder what it does. Bottom-right, where it does not cover an app's own header or nav. */}
      {view === "preview" && (
        <button
          type="button"
          onClick={() => setMaxed((v) => !v)}
          aria-label={maxed ? "Shrink the preview" : "Fill the window"}
          title={maxed ? "Shrink back (Esc)" : "Fill the window"}
          /* SELF-CONTRASTING, and NOT themed — the one control in the product that must ignore our
             tokens. It floats over the CUSTOMER'S APP, whose background we do not control and cannot
             know: `bg-surface/80` made it a pale pill on a pale app and it disappeared (which is how
             this was found — "i don't see the button"). A dark translucent circle with a white glyph is
             what every video player and map uses over unknown content, for exactly this reason, and it
             reads the same in both our themes because the ground is not ours either way. */
          className="absolute bottom-3 right-3 z-20 rounded-full border border-white/20 bg-black/55 p-2 text-white shadow-[0_2px_8px_rgba(0,0,0,0.35)] backdrop-blur transition hover:bg-black/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
        >
          {maxed ? <ShrinkIcon /> : <ExpandIcon size="large" />}
        </button>
      )}
    </div>
  );
}

// Seamless chat column — page background reads through (no card / no
// border) so the column feels part of the page. Layout: message thread
// scrolls in the middle; optional run-status line above the composer
// while a build is in flight; composer at the bottom.
export function ChatColumn({ slug, initialActiveRunId, onRunComplete, attachments = false }: { slug: string; initialActiveRunId?: string | null; onRunComplete?: () => void; attachments?: boolean }) {
  // The hook owns the chat state machine: thread fetch, send dispatch,
  // SSE stream, in-flight tracking. We only render its outputs.
  const chat = useChat(slug, { onRunComplete, initialActiveRunId });

  const scroll = useThreadScroll(chat.messages.length);

  return (
    // clamp(380px, 22vw, 520px): below ~1730 px viewport the chat sits at
    // its 380 px floor (tight enough not to crowd the preview); above
    // ~2360 px (2K+) it caps at 520 px so messages don't span a
    // ridiculous line length on 4K. In between it grows linearly with
    // viewport so the chat keeps a proportional feel.
    <div className="relative flex h-full w-full flex-shrink-0 flex-col overflow-hidden lg:w-[var(--chat-w,clamp(380px,22vw,520px))]">
      <ThreadView chat={chat} scroll={scroll} />

      {/* THE PLAN, DIRECTLY ABOVE THE COMPOSER.
          Where the operator's hands are: starting the next part and typing a request are the same
          gesture at the same moment, and the strip is the thing they steer a multi-part build with.
          (It sat at the top of the column first — far from the input, and pushed off screen by a long
          thread, which is the opposite of always-visible.)
          Derived from the thread by lib/plan_strip, so no extra request and a reloaded page rebuilds
          it. Renders nothing at all when there is no plan. */}
      <PlanStripBar chat={chat} />

      <ComposerArea chat={chat} onPin={scroll.pin} attachments={attachments} />
    </div>
  );
}

/**
 * What the operator wrote while a run worked, waiting for it to end (lib/operator_inbox). Each line can
 * be edited or removed while it waits; once the next run takes it, it is a message of the thread.
 *
 * "Send now" exists for ONE case: a queue left behind with no run working (a reload while the run
 * ended, a platform restart — the server never starts a run on its own). Not while an answer is owed:
 * the server would refuse, and the operator is looking at the question.
 */
function QueuedMessages({ chat }: { chat: ReturnType<typeof useChat> }) {
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null);
  if (chat.inbox.length === 0) return null;
  const leftBehind = !chat.runActive && !chat.sending && !chat.askPending;
  return (
    <div className="mx-4 mb-1 rounded-xl border border-border bg-surface-muted px-3 py-2 text-xs" aria-live="polite">
      <div className="mb-1 flex items-center justify-between text-text-secondary">
        <span>{chat.inbox.length === 1 ? "1 message waits" : `${chat.inbox.length} messages wait`} — {leftBehind ? "no run is working" : "sent when this run ends"}</span>
        {leftBehind && (
          <button type="button" onClick={() => void chat.sendQueuedNow()} className="rounded-full bg-accent px-2.5 py-0.5 text-[11px] font-semibold text-white hover:bg-accent-hover">
            Send now
          </button>
        )}
      </div>
      <ul className="space-y-1">
        {chat.inbox.map((m) => (
          <li key={m.id} className="flex items-start gap-2">
            {editing?.id === m.id ? (
              <>
                <textarea
                  value={editing.text}
                  onChange={(e) => setEditing({ id: m.id, text: e.target.value })}
                  rows={2}
                  aria-label="Edit queued message"
                  className="min-w-0 flex-1 resize-none rounded-md border border-border bg-surface px-2 py-1 outline-none"
                />
                <button type="button" onClick={() => { const t = editing.text; setEditing(null); void chat.editQueued(m.id, t); }} disabled={!editing.text.trim()} className="text-accent disabled:opacity-50">Save</button>
                <button type="button" onClick={() => setEditing(null)} className="text-text-secondary">Cancel</button>
              </>
            ) : (
              <>
                <span className="min-w-0 flex-1 whitespace-pre-wrap break-words">{m.text}</span>
                <button type="button" onClick={() => setEditing({ id: m.id, text: m.text })} aria-label="Edit queued message" className="text-text-secondary hover:text-text">Edit</button>
                <button type="button" onClick={() => void chat.removeQueued(m.id)} aria-label="Remove queued message" className="text-text-secondary hover:text-danger">Remove</button>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The composer and its expanded modal, and the ONLY owner of what the operator is typing.
 *
 * WHY ITS OWN COMPONENT. The text used to live in ChatColumn, so every keystroke re-rendered the
 * column — and with it the whole thread, re-parsing the Markdown of every message. The cost grew
 * with the thread: measured in Chromium on the production build (scripts/verify_keystroke.ts,
 * 2026-10-24), a keystroke took 32 ms at 43 messages, 72 ms at 147 and 288 ms at 678, and the
 * longest live thread on perimetr-node-1 has 1 945. Owned here, a keystroke renders the composer
 * and nothing else. Do not lift `text` back above this component.
 *
 * A FRAGMENT, not a wrapper: the modal positions itself against the column, so the DOM stays
 * exactly what ChatColumn rendered before.
 */
function ComposerArea({ chat, onPin, attachments }: { chat: ReturnType<typeof useChat>; onPin: () => void; attachments: boolean }) {
  // Composer state is lifted here so it survives expand/collapse of the
  // modal — what the operator wrote in the modal carries back to the
  // inline composer (and vice versa).
  // Default to "act" — the primary use is building/changing the app. Flip to
  // "chat" to just ask a question. Explicit, so a question can never trigger a
  // build and a change can never be silently answered.
  const [mode, setMode]     = useState<ChatMode>("act");
  // L'idée tapée sur le site arrive ici (lib/pending_idea), puis l'état est vidé.
  const loc = useLocation();
  const navigate = useNavigate();
  const [text, setText]     = useState(() => ideaFromState(loc.state) ?? "");
  useEffect(() => {
    if (ideaFromState(loc.state)) navigate(loc.pathname + loc.search, { replace: true, state: null });
  }, []);
  // LES CAPTURES JOINTES À CE MESSAGE. Toutes les règles — réduction à 1024 px, deux au
  // plus, types permis, coût en jetons — sont dans src/lib/attachment.ts, éprouvées. Ce
  // crochet ne fait que les appliquer au navigateur.
  const joints = useAttachments();
  // LA CAPTURE EST LENTE : un vrai navigateur démarre côté serveur et attend que la page se
  // pose. Sans cet état, l'opérateur reclique et on lance deux navigateurs pour une image.
  const [expanded, setExp]  = useState(false);

  // Esc closes the modal — standard expectation for any centered overlay.
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setExp(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded]);

  // WRITING WHILE A RUN WORKS (lib/operator_inbox): the words wait for the run to end, then start the
  // next one. Nothing is stopped, nothing is answered by them.
  const queuing = queueMode({ runActive: chat.runActive, askPending: !!chat.askPending, mode });
  async function submit() {
    if (!text.trim()) return;
    if (queuing) {
      const toQueue = text;
      setText("");
      setExp(false);
      onPin();
      try {
        // "no-run": the run ended as this was sent — nothing was queued. The words go back in the
        // composer, which is now a plain one: the operator sends them themselves, seeing whatever
        // the run ended on (a question may be owed, and these words must not become its answer).
        if ((await chat.queue(toQueue)) === "no-run") setText(toQueue);
      } catch {
        setText(toQueue);
      }
      return;
    }
    if (chat.sending) return;
    const toSend = text;
    setText("");        // clear the composer optimistically; restore on error below
    setExp(false);      // collapse the modal if it was open
    // Sending is an explicit "I want to see this" — re-pin to the bottom so the
    // user's own message (and the reply) scroll into view even if they'd
    // scrolled up to read history.
    onPin();
    // Les pièces jointes partent AVEC le message, puis l'ardoise est effacée — sinon la
    // suivante les renverrait, et l'utilisateur paierait deux fois la même image.
    //
    // UN SEUL OBJET pour les deux sortes : la forme du corps est décidée dans
    // src/lib/run_body, qui n'ajoute un champ que s'il y a quelque chose dedans.
    const imagesIci = joints.items.map((a) => a.url);
    const docsIci = joints.documents.map((d) => ({ name: d.name, text: d.text }));
    try {
      await chat.send(toSend, mode, undefined, undefined, { images: imagesIci, documents: docsIci });
      joints.clear();
    } catch {
      setText(toSend);  // restore on failure so the operator can retry without retyping
      // Les captures RESTENT : l'envoi n'a pas eu lieu, et les joindre à nouveau serait une
      // corvée que rien ne justifie.
    }
  }

  return (
    <>
      <QueuedMessages chat={chat} />
      <div className="px-4 pb-4 pt-2">
        <ChatComposer
          size="compact"
          mode={mode} onModeChange={setMode}
          text={text} onTextChange={setText}
          onExpand={() => setExp(true)}
          onSend={submit}
          sending={chat.sending}
          runActive={chat.runActive}
          onAbort={chat.abort}
          askPending={chat.askPending}
          onPickOption={(i) => { setText(""); void chat.answerOption(i); }}
          // A queued message is TEXT: an image would ride the next run's model call, which is not
          // open yet. The clip comes back once the run ends; what was attached stays attached.
          attachments={attachments && !queuing ? joints : undefined}
          queuing={queuing}
        />
      </div>

      {expanded && (
        <ExpandedComposerModal
          mode={mode} onModeChange={setMode}
          text={text} onTextChange={setText}
          onClose={() => setExp(false)}
          onSend={submit}
          sending={chat.sending}
          runActive={chat.runActive}
          onAbort={chat.abort}
          askPending={chat.askPending}
          onPickOption={(i) => { setText(""); setExp(false); void chat.answerOption(i); }}
          attachments={attachments && !queuing ? joints : undefined}
          queuing={queuing}
        />
      )}
    </>
  );
}

/**
 * The thread's scrolling, shared by the OWNER's column and the READ-ONLY one (a shared app's
 * viewer) — one mechanism, so the two can never scroll differently.
 *
 * Auto-scroll, but ONLY when the reader is already at the bottom. If they've scrolled up to read
 * earlier output, a new message must NOT yank them back down — that "jump" made history
 * unreadable while a run was live.
 *
 * The at-bottom decision lives in a ref (updated synchronously on scroll) so the scroll effect
 * never reads a stale value and never re-runs just because the flag changed; a separate state
 * drives the "jump to latest" button.
 */
function useThreadScroll(messageCount: number) {
  const bottomRef  = useRef<HTMLDivElement | null>(null);
  const scrollRef  = useRef<HTMLDivElement | null>(null);
  const atBottomRef = useRef(true);
  const [showJump, setShowJump] = useState(false);

  // "Close enough" to the bottom counts as pinned — avoids fighting sub-pixel
  // rounding and the last line's padding.
  const BOTTOM_THRESHOLD = 48;

  const onThreadScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < BOTTOM_THRESHOLD;
    atBottomRef.current = nearBottom;
    setShowJump((prev) => (prev === !nearBottom ? prev : !nearBottom));
  };

  const jumpToLatest = () => {
    bottomRef.current?.scrollIntoView({ block: "end", behavior: "smooth" });
    atBottomRef.current = true;
    setShowJump(false);
  };

  /** Re-pin to the bottom without scrolling now — the next message will scroll into view. */
  const pin = () => {
    atBottomRef.current = true;
    setShowJump(false);
  };

  useEffect(() => {
    if (atBottomRef.current) bottomRef.current?.scrollIntoView({ block: "end" });
    // Follows the MESSAGES only. It also followed `currentAction` while a pinned live-action line
    // existed above the composer; that line was a duplicate of the feed's own chips and is gone, and a
    // tool call appends no message — so there is nothing new to scroll to between turns.
  }, [messageCount]);

  return { bottomRef, scrollRef, showJump, onThreadScroll, jumpToLatest, pin };
}

/** The thread itself — the grouped feed and its "jump to latest" — for either column. */
function ThreadView({ chat, scroll }: { chat: ReturnType<typeof useChat>; scroll: ReturnType<typeof useThreadScroll> }) {
  // The column re-renders on every run event (a new status line, a new message). The rows depend
  // on three things only, so they are derived again only when one of them changes.
  const awaitingAnswer = !!chat.askPending;
  const rows = useMemo(
    () => groupFeed(chat.messages, { live: chat.runActive, awaitingAnswer }),
    [chat.messages, chat.runActive, awaitingAnswer],
  );
  return (
    <>
      {/* The scroller gets its OWN positioning context so "jump to latest" can sit just above the
          composer instead of on top of it: anchored to the column, `bottom-3` was 12px from the bottom
          of the WHOLE column, which is inside the composer. Anchored to the thread, the same 12px is
          12px above where the thread ends — which is where the operator's eye already is.
          The wrapper is the anchor and the inner div still scrolls, so the button does not scroll away. */}
      <div className="relative min-h-0 flex-1">
      <div ref={scroll.scrollRef} onScroll={scroll.onThreadScroll} className="thread-scroll h-full overflow-y-auto px-5 py-6">
        {chat.loading ? (
          <div className="text-center text-[11px] text-text-secondary">Loading…</div>
        ) : chat.messages.length === 0 ? (
          <div className="text-center text-[11px] text-text-secondary">No messages yet.</div>
        ) : (
          <div className="space-y-4">
            {/* GROUPED, not per-message. A run posts one item per agent turn plus one
                reasoning item before each: ~300 rows for a build like `rounds`. Folding
                each of them individually would only turn 300 bubbles into 300 fold bars,
                so lib/run_feed groups the chatter between milestones into ONE row and
                leaves the milestones — the platform's verdict, a question the run waits
                on, the operator's own words — standing. `runActive` keeps the row being
                written to OPEN, so the feed never folds under the operator's eyes. */}
            {rows.map((row, i) => <FeedRowView key={i} row={row} />)}
            <div ref={scroll.bottomRef} />
          </div>
        )}
        {chat.error && (
          <div className="mt-3 rounded-lg border border-danger-border bg-danger-soft p-2 text-[11px] text-danger">
            {chat.error}
          </div>
        )}
      </div>

      {/* Jump-to-latest — only shown when scrolled up, so the reader can stay
          in the history AND get back to live in one click. */}
      {scroll.showJump && (
        <button
          type="button"
          onClick={scroll.jumpToLatest}
          className="absolute bottom-3 left-1/2 z-10 -translate-x-1/2 rounded-full border border-border bg-surface px-3 py-1 text-[11px] text-text-secondary shadow-[var(--shadow-raised-sm)] hover:text-text"
        >
          ↓ Jump to latest
        </button>
      )}
      </div>

    </>
  );
}

// One message in the thread. User turns sit right with a subtle accent
// background; assistant turns sit left as plain text — the page is the
// surface, so there's no "card" wrapping the assistant copy.
//
// For build-mode assistant turns we additionally extract the embedded
// tool calls + SEARCH/REPLACE blocks (which would otherwise dump JSON
// into the chat verbatim) and render them as small monospace chips
// BELOW the prose, in the order the LLM emitted them. The orchestrator
// has already dispatched these actions server-side — the chips are
// purely a UI surface so the operator can see what the assistant did.
// One row of the grouped feed.
//
// EXHAUSTIVE on purpose. There is no component-test harness in platform-ui, so a row
// kind added without a branch here could never be caught by a test — it would simply
// stop appearing in the feed, which for a milestone means hiding the reason a run is
// stuck. The `never` assignment in the default branch makes that a COMPILE error
// instead: the compiler checks what no test here can.
function FeedRowView({ row }: { row: FeedRow }) {
  switch (row.kind) {
    case "milestone": return <MessageBubble m={row.item} />;
    case "steps":     return <StepsBlock row={row} />;
    default: {
      const _exhaustive: never = row;
      return _exhaustive;
    }
  }
}

// The folded chatter of one lane between two milestones.
//
// Native <details>, exactly like ReasoningBlock: no JS state, keyboard-accessible,
// and `open` driven by run_feed (the live group stays open). The summary line is built
// by lib/run_feed.summaryLabel — facts only, tested there — and never says "show more":
// the arrow already means that.
function StepsBlock({ row }: { row: Extract<FeedRow, { kind: "steps" }> }) {
  return (
    <details className="group rounded-lg border border-border bg-surface-muted/40" open={row.open}>
      <summary className="flex cursor-pointer select-none list-none items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-text-secondary">
        <svg
          viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor"
          strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
          className="transition-transform group-open:rotate-90"
        >
          <polyline points="9 6 15 12 9 18" />
        </svg>
        {/* WHICH lane wrote these steps, using the same badge the bubbles and the live
            indicator use — the two lanes run in parallel into one thread, and a summary
            whose lane is unnamed lets a frontend refusal read as a backend problem. */}
        {row.lane && <ActionChipBadge chip={row.lane} />}
        <span className="normal-case tracking-normal">{summaryLabel(row.summary)}</span>
      </summary>
      <div className="space-y-4 border-t border-border px-2.5 py-2">
        {row.items.map((m, i) => <MessageBubble key={i} m={m} />)}
      </div>
    </details>
  );
}

function MessageBubbleView({ m }: { m: Message }) {
  // Client-synthesized "thinking" item (from the run's `reasoning` SSE
  // event). Rendered as a collapsed-by-default disclosure so the operator
  // can peek at the model's chain-of-thought without it dominating the
  // thread — the same collapse/expand affordance as Claude Code's thinking.
  if (m.kind === "reasoning") {
    return <ReasoningBlock text={m.text} tokens={m.reasoning_tokens ?? 0} />;
  }
  // The PLATFORM's own verdict on whether the app was proved to work, sitting
  // beside the agent's closing words. Distinct from an assistant bubble on
  // purpose: this is the machine's judgment, and it is the one line in the
  // thread the agent did not write.
  if (m.kind === "verification") {
    return <VerificationNote text={m.text} />;
  }
  // The plan the operator approved, and the part boundaries that give the run its spine. Rendered
  // BEFORE the corruption guard and the action extraction below because neither applies: these are
  // built by the client from typed events, not parsed out of a model's prose.
  if (m.kind === "plan")  return <PlanCard m={m} />;
  if (m.kind === "part")  return <PartHeader m={m} />;
  if (m.kind === "failure") return <FailureNote text={m.text} />;
  if (m.kind === "plan-closed") return <PlanClosedNote text={m.text} remaining={m.plan_remaining ?? []} />;
  if (m.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[88%] rounded-2xl bg-accent-soft px-3 py-2 text-[12px] leading-relaxed text-text">
          {m.text}
        </div>
      </div>
    );
  }
  // Hide entire message when the raw text shows provider-side
  // corruption. Done BEFORE extractActions so a corrupted turn
  // produces no bubble AND no tool chips (a leaked tool_call tag
  // inside a corrupted turn is also garbage).
  if (m.mode === "build" && isCorrupted(m.text)) return null;
  // Only build-mode assistant turns carry tool / edit blocks. Chat-
  // mode replies are pure prose — no extraction needed.
  const { prose, actions } = m.mode === "build"
    ? extractActions(m.text)
    : { prose: m.text, actions: [] as Action[] };
  // BUILD-MODE PROSE — operator-facing narration the model emits
  // alongside its tool calls ("now writing the contract", "deploying
  // the changes", "verifying the route"). Used to be suppressed
  // entirely (build mode was SILENT — tool chips only) on the
  // theory that the chips carried the signal and the prose was
  // noise. Operator feedback (2026-06-18): silent build feels
  // opaque — the operator wants short narration so they can
  // follow what's happening. The system prompt (v61+) instructs
  // the model to keep narration to ONE short sentence per fence,
  // so what reaches the UI is short by construction; the prompt
  // bears the brevity discipline, the UI just renders honestly.
  //
  // ASK STILL TAKES PRIORITY: when the model fires `ask`, its
  // question is what the operator needs to read + reply to —
  // surface that as the prose instead of whatever narration the
  // model put before the fence. Without this, an "ask" turn that
  // ALSO had a stray narration line would bury the question.
  //
  // A build-mode turn with NO actions and NO prose collapses to
  // just the "Assistant · build" header. We hide the header too
  // in that case so an empty turn renders as nothing at all
  // rather than a floating label.
  const askQuestion = m.mode === "build" ? extractAskQuestion(actions) : null;
  const buildProse  = askQuestion !== null ? askQuestion : prose;
  const showProse   = m.mode === "chat" ? prose.length > 0 : buildProse.length > 0;
  const proseText   = m.mode === "chat" ? prose : buildProse;
  const showActions = actions.length > 0;
  if (!showProse && !showActions) return null;
  return (
    <div className="max-w-[92%] text-[12px] leading-relaxed text-text">
      {/* WHICH AGENT wrote this turn, kept next to its words for good.
          The lane used to live only on the live action indicator, which the
          next tool call replaced — so during a parallel build the operator
          could see that SOMETHING was happening but not who, and a reload
          erased even that. The badge is the SAME component the indicator
          uses, so "frontend is the accent one" means one thing in both
          places. No badge at all when the lane is unknown (a chat reply, a
          single-agent run, a turn older than the field) — never a guess. */}
      <div className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-text-secondary">
        <span>{m.mode === "build" ? "Assistant · build" : "Assistant"}</span>
        {m.lane && <ActionChipBadge chip={m.lane} />}
      </div>
      {showProse && (
        <div className="markdown">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{proseText}</ReactMarkdown>
        </div>
      )}
      {showActions && (
        // Horizontal scroll, not wrap. Long turns (10+ tools batched
        // in one fence) used to stack 3-4 rows tall and dominate the
        // bubble — visually overwhelming for a transient signal. Now
        // they stay one row and the operator scrolls if curious.
        // `min-w-0` lets the scroll container shrink inside its flex
        // parent so `overflow-x-auto` actually engages.
        //
        // `.no-scrollbar` (index.css): the bar under a single 20px-tall row of chips was taller than the
        // gap it sat in, and it appeared on every turn that happened to batch a few tools — a moving
        // line of chrome down the middle of the conversation. The row still scrolls by wheel, trackpad
        // and touch; unlike the thread, there is no "how much is above me" to convey, so nothing is
        // lost by hiding it.
        <div className={`no-scrollbar flex gap-1.5 overflow-x-auto min-w-0 ${showProse ? "mt-2" : ""}`}>
          {actions.map((a, i) => (
            <span
              key={i}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border bg-surface px-2 py-0.5 text-[10px] font-mono text-text-secondary"
              title={a.kind === "tool" ? JSON.stringify(a.input) : a.path}
            >
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent" aria-hidden="true" />
              {actionLabel(a)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// MEMOIZED, and this is what keeps a live run from freezing the console. Every run event re-renders
// the column, and a bubble's render parses its Markdown — measured at ~0.6 ms a message, so a
// 678-message thread blocked the main thread ~320 ms per event and the keystrokes that landed in
// that window waited for it (scripts/verify_keystroke.ts, run mode, 2026-10-24). useChat only ever
// APPENDS (`[...prev, x]`), so an existing message keeps its identity and its bubble is skipped;
// a reload replaces the array and renders once, as it must.
const MessageBubble = memo(MessageBubbleView);

// THE PLAN AS A STRIP — the parts of a multi-part build, and the click that starts the next one.
//
// Above the chat and always visible, because it is the thing the operator steers with. The alternative
// it replaced was pasting each part's text back into the composer, which also made the platform
// propose a brand-new plan and start again at part 1.
//
// What a click does NOT do: re-decide anything. It names the part; the server reads that part's text
// from the plan it holds. And `canStart` (lib/plan_strip, tested) decides which rows are live, so the
// strip never invites a click that would come back as a conflict.
function PlanStripBar({ chat }: { chat: ReturnType<typeof useChat> }) {
  const strip = planStripOf(chat.messages);
  if (strip.total === 0) return null;
  const hint = suggestedPart(strip);
  return (
    <div className="border-t border-border bg-surface-muted/40 px-4 pb-1 pt-2">
      {/* NO HEADING. It said "The plan · 1 / 5 · building…" and every word of it was already on the
          cards: the ticks say how many are done, the total is the number of cards, and the ▸ marks the
          one in flight. A label that repeats what is beside it is chrome in the one place the operator
          looks most. */}
      {/* A horizontal strip that scrolls rather than wraps: a ten-part plan must not push the chat
          down the screen, and the parts are a sequence — reading them in a line matches that. */}
      {/* Scrolls horizontally with NO visible scrollbar (.no-scrollbar, index.css): a 10-part plan must
          not push the chat off the screen, and a scrollbar under a row of buttons is noise sitting
          exactly where the eye goes. The row is still scrollable by wheel, trackpad, touch and — since
          each startable part is a real <button> — by keyboard focus. */}
      <div className="flex items-center gap-2">
        <div className="no-scrollbar flex flex-1 gap-2 overflow-x-auto pb-1">
          {strip.parts.map((p) => (
            <StripCard
              key={p.id}
              part={p}
              startable={canStart(strip, p.id)}
              suggested={hint?.id === p.id}
              onStart={() => { void chat.startPart(p.id); }}
            />
          ))}
        </div>
        {/* CLOSE THE PLAN. Outside the scrolling row so it stays reachable on a ten-part plan, and as
            quiet as the row allows: no border, no fill, muted until hover. It is not an alarm — the
            operator is finished, not in trouble.

            No confirmation dialog, and that is a judgement rather than an omission: nothing built is
            lost, the parts that were never built are named in the line this leaves behind, and the plan
            card further up the thread still lists every one of them. The title says exactly that. */}
        <button
          type="button"
          onClick={() => { void chat.closeThePlan(); }}
          disabled={strip.busy}
          title={strip.busy
            ? "A part is being built — stop the run first"
            : "Close this plan. The parts you have not built are listed in the note it leaves."}
          className="shrink-0 rounded px-1.5 py-1 text-[11px] text-text-secondary hover:text-text disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          Close
        </button>
      </div>
    </div>
  );
}

// One part in the strip.
//
// A real <button> when it can be started and a plain <div> when it cannot — not a button with a
// disabled attribute and not a div with a click handler. A finished part is not a control the keyboard
// should stop on, and a startable part must be reachable without a mouse.
export function StripCard({ part, startable, suggested, onStart }: { part: StripPart; startable: boolean; suggested: boolean; onStart: () => void }) {
  const mark = part.state === "sealed" ? "✓" : part.state === "failed" ? "✗" : part.state === "running" ? "▸" : "○";
  const tone =
    part.state === "sealed" ? "border-border text-text-secondary"
    : part.state === "failed" ? "border-danger-border text-danger dark:text-danger"
    : part.state === "running" ? "border-accent text-text"
    : "border-border text-text";
  const body = (
    <>
      <span className="font-semibold">{mark}</span>
      <span className="tabular-nums text-text-secondary">{part.index}</span>
      <span className="max-w-[16rem] truncate">{part.title}</span>
      {part.ms !== null && <span className="tabular-nums text-text-secondary">{formatDuration(part.ms)}</span>}
    </>
  );
  const shared = `flex shrink-0 items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] ${tone}`;
  if (!startable) {
    return <div className={shared} title={part.state === "sealed" ? "Finished" : part.state === "running" ? "Building now" : "Wait for the current part to finish"}>{body}</div>;
  }
  return (
    <button
      type="button"
      onClick={onStart}
      title={part.state === "failed" ? "Try this part again" : "Build this part"}
      className={`${shared} cursor-pointer hover:bg-accent-soft/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent ${suggested ? "ring-1 ring-accent" : ""}`}
    >
      {body}
    </button>
  );
}

// THE PLAN WAS CLOSED — and what was left in it.
//
// Quiet on purpose: nothing went wrong, the operator simply decided they had what they wanted. But the
// parts they never built are NAMED, because the strip was the only other place they were listed and it
// has just disappeared. A count alone would leave them unable to tell whether the one they cared about
// was among them.
function PlanClosedNote({ text, remaining }: { text: string; remaining: string[] }) {
  return (
    <div className="text-[11px] leading-relaxed text-text-secondary">
      <span className="font-semibold uppercase tracking-wider text-[10px]">{text}</span>
      {remaining.length > 0 && (
        <ul className="ml-4 mt-1 list-disc">
          {remaining.map((t, i) => <li key={i}>{t}</li>)}
        </ul>
      )}
    </div>
  );
}

// THE BUILD STOPPED, and why.
//
// The text is the server's, verbatim (lib/failure_notice). Styled as a warning rather than an error:
// the sentence itself says nothing is lost, and a red panel would contradict its own words.
//
// Not a <details>. The whole reason this component exists is that a build stopped and the thread said
// nothing — a folded explanation is the same silence with an arrow on it.
function FailureNote({ text }: { text: string }) {
  return (
    <div className="rounded-lg border border-warn-border/60 bg-warn-soft/60 px-3 py-2 text-[12px] leading-relaxed text-text dark:bg-warn-strong/10">
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-warn dark:text-warn">
        The build stopped
      </div>
      {text}
    </div>
  );
}

// THE PLAN, as the operator approved it.
//
// Their own requirements first, then the parts in build order. The requirements are there for one
// reason: the coverage check proves no LISTED requirement was dropped, and it cannot prove the list
// itself is faithful to what they wrote. So the list is shown, and the only reader who can catch a
// bad paraphrase gets to read it.
//
// Not a <details>: a plan folded by default is an approval nobody saw.
function PlanCard({ m }: { m: Message }) {
  const parts = m.plan_parts ?? [];
  const reqs = m.plan_requirements ?? [];
  return (
    <div className="rounded-lg border border-accent/40 bg-accent-soft/30 px-3 py-2.5 text-[12px] leading-relaxed text-text">
      <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-accent">
        The plan · {parts.length} part{parts.length === 1 ? "" : "s"}, one after another
      </div>
      <ol className="ml-4 list-decimal space-y-0.5">
        {parts.map((part) => <li key={part.id}>{part.title}</li>)}
      </ol>
      {reqs.length > 0 && (
        <details className="group mt-2">
          <summary className="cursor-pointer select-none list-none text-[10px] font-semibold uppercase tracking-wider text-text-secondary">
            What you asked for · {reqs.length}
          </summary>
          <ul className="ml-4 mt-1 list-disc space-y-0.5 text-text-secondary">
            {reqs.map((t, i) => <li key={i}>{t}</li>)}
          </ul>
        </details>
      )}
    </div>
  );
}

// A PART BOUNDARY — "3 / 10 · The drivers and their list".
//
// Three facts and not one more: where we are, what this part is, and its state. The duration
// appears only when the part is closed AND the run reported one: it is what lets the operator guess
// when the plan will finish, and a fabricated "0s" would make that guess wrong (Message.part_ms).
function PartHeader({ m }: { m: Message }) {
  const state = m.part_state ?? "running";
  const mark = state === "sealed" ? "✓" : state === "failed" ? "✗" : "▸";
  const tone =
    state === "sealed" ? "text-accent"
    : state === "failed" ? "text-danger"
    : "text-text";
  const took = formatDuration(m.part_ms ?? null);
  return (
    <div className="flex items-center gap-2 text-[11px]">
      <span className={`font-semibold ${tone}`}>{mark}</span>
      <span className="font-semibold tabular-nums text-text-secondary">{m.part_index} / {m.part_total}</span>
      <span className={`flex-1 truncate ${tone}`}>{m.part_title}</span>
      {took && <span className="tabular-nums text-text-secondary">{took}</span>}
      <span aria-hidden="true" className="h-px flex-1 bg-border" />
    </div>
  );
}

// Collapsible "thinking" block for a reasoning-model turn. Native
// <details> so collapse/expand needs no JS state and stays keyboard-
// accessible (Space/Enter toggles the summary); collapsed by default so a
// long chain-of-thought never dominates the thread. The chevron rotates on
// open via the `group-open` variant. Styled only with platform-ui semantic
// tokens (border-border, surface-muted, text-secondary) so it reads in
// every theme. The thought text is pre-wrapped + vertically scroll-capped
// so a 2k-token ramble can't blow out the column height.
function ReasoningBlock({ text, tokens }: { text: string; tokens: number }) {
  // A provider that reports no separate reasoning-token count sends 0 — we
  // then label it just "Thought" rather than the dishonest "0 tokens".
  const label = tokens > 0 ? `Thought for ${tokens.toLocaleString()} tokens` : "Thought";
  return (
    <details className="group rounded-lg border border-border bg-surface-muted/40">
      <summary className="flex cursor-pointer select-none list-none items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-text-secondary">
        <svg
          viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor"
          strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
          className="transition-transform group-open:rotate-90"
        >
          <polyline points="9 6 15 12 9 18" />
        </svg>
        {label}
      </summary>
      <div className="max-h-64 overflow-y-auto whitespace-pre-wrap border-t border-border px-2.5 py-2 font-mono text-[11px] leading-relaxed text-text-secondary">
        {text}
      </div>
    </details>
  );
}

// VerificationNote — the platform's verdict on a finished run.
//
// A run ends with the agent's summary: free prose the operator reads as fact,
// and prose is not checkable. canva, 2026-09-06, closed with "No JSX syntax
// error was present; all tags are properly closed" — true of the app, false of
// the record — while the machine's verdict for that run was UNSUPPORTED and was
// published to nobody.
//
// Rendered in the platform's own semantic tokens, never a bubble: an operator
// must be able to tell at a glance which line the agent wrote and which one the
// machine did. A verdict that could not verify carries the `border-active`
// accent the rest of the UI uses for "look here"; a confirmation stays quiet.
function VerificationNote({ text }: { text: string }) {
  const proved = text.startsWith("Checked by the platform");
  return (
    <div
      className={
        "flex items-start gap-2 rounded-md border px-2.5 py-1.5 text-[11px] leading-relaxed " +
        (proved
          ? "border-border bg-surface text-text-secondary"
          : "border-active bg-surface text-text")
      }
      role="note"
    >
      <span aria-hidden="true" className="mt-[2px] font-mono text-[10px] text-text-tertiary">
        {proved ? "\u2713" : "!"}
      </span>
      <span>{text}</span>
    </div>
  );
}

// Chip badge for the action indicator. Three styles map to the three
// phases, using ONLY the platform-ui's semantic tokens (border-border,
// border-active, accent, accent-soft, text-secondary, text-tertiary)
// so the chip stays readable across every theme — no hardcoded hex
// or palette utilities.
//
//   backend  — neutral tone (matches the existing indicator palette
//              so backend events feel familiar; legacy /run flows
//              would render with this default if they ever surfaced
//              a chip)
//   frontend — accent tone (visually distinct from backend so the
//              eye picks up parallel-build interleaving on the single
//              action indicator)
//   merge    — border-active tone (the merge phase is verification +
//              heal — operator should notice it's a different phase
//              than the build; using border-active rather than accent
//              keeps it distinct from the frontend chip)
function ActionChipBadge({ chip }: { chip: NonNullable<CurrentAction["chip"]> }) {
  const styles: Record<NonNullable<CurrentAction["chip"]>, string> = {
    backend:  "border border-border bg-surface text-text-secondary",
    frontend: "border border-accent bg-accent-soft text-accent",
    merge:    "border border-border-active bg-surface-muted text-text",
  };
  return (
    <span
      className={`rounded px-1 text-[9px] uppercase tracking-wider ${styles[chip]}`}
      aria-label={`${chip} agent`}
    >
      {chip}
    </span>
  );
}

// AskOptionsPanel — rendered above the composer textarea when the
// backend agent fired `ask`. Two shapes driven by `multiSelect`:
//   - false → radio group; clicking an option REPLACES the textarea
//             (single answer semantics)
//   - true  → checkbox group; ticking accumulates comma-joined options
//             into the textarea (multi answer semantics), preserving
//             the option order the model chose
// The textarea remains editable in BOTH modes — an operator who wants
// to type "actually, let's do something different" always can. The
// options are a fast-path, not a lock-in.
//
// The panel derives its "selected" state from the CURRENT textarea
// text (not local state). This keeps the source of truth single
// (the parent's `text` prop) so a free-form edit invalidating the
// current selection is naturally reflected — the ticked option
// simply un-ticks when the text no longer matches.
function AskOptionsPanel({ size, askPending, onPick, onWriteInstead, busy: sendingNow, waiting }: {
  size:           "compact" | "large";
  askPending:     AskPending;
  /** One gesture: picking SENDS. */
  onPick:         (index: number) => void;
  /** The way out — reveals the composer for an answer in the operator's own words. */
  onWriteInstead: () => void;
  busy:           boolean;
  /** The run is still FINISHING: the other agent has not stopped yet, and the server would refuse
   *  an answer (409). Shown, read-only, and said — not a set of buttons that silently do nothing
   *  (9 of October's 12 refusals, design_questions_operateur.md). */
  waiting:        boolean;
}) {
  const isLarge = size === "large";
  const { question, options } = askPending;
  const busy = sendingNow || waiting;

  // Number keys pick, as they do in a terminal picker. Registered on the panel rather than the
  // window so it cannot fire while the operator is typing somewhere else on the page.
  function onKey(e: React.KeyboardEvent<HTMLDivElement>) {
    if (busy) return;
    const n = Number(e.key);
    if (Number.isInteger(n) && n >= 1 && n <= Math.min(9, options.length)) {
      e.preventDefault();
      onPick(n - 1);
    }
  }

  return (
    <div
      onKeyDown={onKey}
      role="group"
      aria-label={question}
      className={isLarge ? "px-5 py-4" : "px-4 py-3"}
    >
      <div className={isLarge ? "mb-3 text-sm font-medium text-text" : "mb-2.5 text-[13px] font-medium text-text"}>
        {question}
      </div>
      {waiting && (
        <div role="status" className={isLarge ? "mb-2 text-[12px] text-text-secondary" : "mb-2 text-[11px] text-text-secondary"}>
          The agents are finishing — you can answer in a moment.
        </div>
      )}
      {!waiting && (askPending.remaining ?? 0) > 0 && (
        <div className={isLarge ? "mb-2 text-[12px] text-text-tertiary" : "mb-2 text-[11px] text-text-tertiary"}>
          {askPending.remaining === 1 ? "One more question after this one." : `${askPending.remaining} more questions after this one.`}
        </div>
      )}
      <div className="flex flex-col gap-1">
        {options.map((opt, i) => (
          <button
            key={i}
            type="button"
            autoFocus={i === 0}
            disabled={busy}
            onClick={() => onPick(i)}
            className={[
              "group flex w-full items-center gap-2.5 rounded-lg border border-transparent text-left transition",
              isLarge ? "px-3 py-2 text-sm" : "px-2.5 py-1.5 text-[12px]",
              busy
                ? "cursor-default opacity-50"
                : "hover:border-border hover:bg-surface-2 focus:border-accent focus:bg-accent-soft focus:outline-none",
            ].join(" ")}
          >
            <span className={[
              "shrink-0 rounded border border-border text-center font-mono text-text-secondary",
              isLarge ? "w-5 text-[11px] leading-5" : "w-4 text-[10px] leading-4",
            ].join(" ")}>{i + 1}</span>
            <span className="text-text">{opt}</span>
          </button>
        ))}
        {/* The way out, as a row of the list rather than a hidden affordance: writing is a
            deliberate branch, not the default. It is what reveals the composer at all. */}
        <button
          type="button"
          disabled={busy}
          onClick={onWriteInstead}
          className={[
            "flex w-full items-center gap-2.5 rounded-lg border border-transparent text-left text-text-secondary transition",
            isLarge ? "px-3 py-2 text-sm" : "px-2.5 py-1.5 text-[12px]",
            busy ? "cursor-default opacity-50" : "hover:border-border hover:bg-surface-2 focus:border-accent focus:outline-none",
          ].join(" ")}
        >
          <span className={[
            "shrink-0 rounded border border-border text-center font-mono",
            isLarge ? "w-5 text-[11px] leading-5" : "w-4 text-[10px] leading-4",
          ].join(" ")}>↵</span>
          <span>Write something else…</span>
        </button>
      </div>
    </div>
  );
}

// One composer, two sizes:
//   - "compact": fits inside the chat column (rows=3, ~3D bar look).
//   - "large":   modal version (rows=15, same 3D look, more room to think).
// The 3D bar (gradient face, inset highlight, drop shadow) is the same in
// both — the modal feels like the inline composer "grew", not like a
// different surface.
export function ChatComposer({
  size, mode, onModeChange, text, onTextChange, onExpand, onCollapse, onSend, sending,
  runActive, onAbort, askPending, onPickOption, attachments, queuing = false,
}: {
  size:         "compact" | "large";
  mode:         ChatMode;
  /** Absent : pas de sélecteur de mode (la page d'accueil, où il n'y a qu'une chose à faire). */
  onModeChange?: (m: ChatMode) => void;
  text:         string;
  onTextChange: (t: string) => void;
  onExpand?:    () => void;        // shown only on the compact variant
  onCollapse?:  () => void;        // shown only on the large variant
  onSend?:      () => void;        // composer is read-only when omitted
  sending?:     boolean;
  // While a build run is streaming, Send is hidden / replaced by Abort.
  // The Abort button calls the server, which cancels the run mid-stream.
  runActive?:   boolean;
  onAbort?:     () => void;
  // When set, the backend agent has fired `ask` and is waiting for the
  // operator's reply. Composer renders the options (radios or
  // checkboxes) above the textarea and shows Send even while runActive
  // is true — otherwise the operator can't reply through the streaming
  // pipeline (see hook: readAskPendingFromEvent).
  askPending?:  AskPending | null;
  /** Answer by CLICKING option `i`. One gesture — this sends. */
  onPickOption?: (index: number) => void;
  /** Les captures de ce message, ou ABSENT quand joindre n'est pas permis.
   *
   *  Un état absent plutôt qu'un drapeau : il n'y a alors rien à montrer ET rien à envoyer,
   *  donc aucune branche où un bouton pourrait apparaître par erreur. C'est ainsi que la
   *  règle « pas de multimodal hors du mode pas-cher » arrive jusqu'ici. */
  attachments?: Attachments;
  /** A run works and the operator is writing anyway: Send QUEUES (lib/operator_inbox queueMode), the
   *  box stays open, and Abort stays beside it. */
  queuing?:     boolean;
}) {
  const isLarge   = size === "large";
  // The operator chose "write something else". Reset whenever a NEW question arrives, so the
  // composer does not stay open across questions — answering by hand once must not opt them out
  // of every future picker.
  const [writingInstead, setWritingInstead] = useState(false);
  useEffect(() => { setWritingInstead(false); }, [askPending?.question]);
  // PARLER AU LIEU D'ÉCRIRE. Le micro AJOUTE à la boîte (joinDictated), il ne la réécrit
  // jamais : quelqu'un peut taper pendant qu'il parle, et son texte doit survivre. Toutes les
  // règles sont dans src/lib/dictation.ts, éprouvées ; ici il n'y a que le branchement.
  const dictation = useDictation((chunk) => onTextChange(joinDictated(text, chunk)));
  const fileRef = useRef<HTMLInputElement>(null);
  /** L'index de la pièce jointe en cours d'annotation, ou `null`.
   *
   *  UN INDEX, PAS L'OBJET : la liste peut changer pendant qu'on dessine (un retrait, un
   *  ajout), et un objet gardé de côté annoterait une image qui n'est plus celle qu'on
   *  croit. L'index est relu dans la liste courante à chaque rendu — s'il ne désigne plus
   *  rien, l'éditeur ne s'ouvre pas.
   *
   *  ICI et non dans la colonne : c'est ce composant qui porte les vignettes, donc le clic.
   *  L'état vit à côté de ce qui le déclenche. */
  const [aDessiner, setADessiner] = useState<number | null>(null);
  // While QUEUING the run's own send is still in flight (`sending` stays up for the whole stream):
  // that must not close the box the operator is writing in.
  const canSend   = !!onSend && (queuing || !sending) && text.trim().length > 0;
  // When an ask is pending, override the runActive → Abort mapping so
  // the operator can actually reply. The pipeline is still technically
  // running (frontend may be inflight after spec_done; backend paused
  // its loop) but the immediate operator action is REPLY, not abort.
  const showAbort = !!runActive && !askPending;
  // Enter sends; Shift+Enter inserts a newline. Standard chat shortcut
  // (ChatGPT / Claude UI / Bolt / v0). The textarea's default for plain
  // Enter is newline-insert — we preventDefault so it fires Send instead.
  function onKey(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (!onSend) return;
    if (e.key === "Enter" && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      if (canSend) onSend();
    }
  }
  return (
    // 3D composer bar. Solid opaque surface (bg-surface = #FFFFFF) —
    // earlier gradient versions read as translucent over the modal
    // backdrop, especially when one stop resolved to transparent. Depth
    // now comes purely from the inset white highlight (top edge catches
    // light) and the outer drop shadow (lifts off the page). Mode toggle
    // sits bottom-left, expand/collapse + Send sit bottom-right.
    <div className="relative rounded-2xl border border-border bg-surface shadow-[0_2px_10px_rgba(0,0,0,0.08)]">
      {/* A QUESTION IS A MODE, NOT A DECORATION.
          While one is pending the composer is REPLACED: no textarea, no Send. Picking an option
          sends it, in one gesture. The old panel sat above a live textarea and a click merely
          wrote the option's words into it — so the operator had to press Send, the choice was
          never transmitted, and the server read it back out of a sentence. "Write something else"
          is the one row that brings the composer back. */}
      {askPending && !writingInstead ? (
        <AskOptionsPanel
          size={size}
          askPending={askPending}
          busy={!!sending}
          waiting={!!runActive}
          onPick={(i) => onPickOption?.(i)}
          onWriteInstead={() => setWritingInstead(true)}
        />
      ) : (
      <>
      {/* LES CAPTURES JOINTES. Leur coût n'est plus affiché (2026-10-18) : le client ne voit
          que son solde en dollars de crédit. */}
      {/* L'ÉDITEUR, ouvert sur l'image qu'on a cliquée. Il reçoit l'ORIGINALE, pas l'image
          affichée : les traits déjà posés sont redessinés depuis `strokes`, et lui passer la
          version annotée les ferait apparaître deux fois. */}
      {attachments !== undefined && aDessiner !== null && attachments.items[aDessiner] !== undefined && (
        <AnnotateOverlay
          url={attachments.items[aDessiner]!.original}
          width={attachments.items[aDessiner]!.width}
          height={attachments.items[aDessiner]!.height}
          strokes={attachments.items[aDessiner]!.strokes}
          onClose={() => setADessiner(null)}
          onSave={(traits) => { const i = aDessiner; setADessiner(null); void attachments.setStrokes(i, traits); }}
        />
      )}
      {/* LA BANDE DES PIÈCES JOINTES. Elle s'ouvre dès qu'il y a UNE pièce, image ou
          document : le compteur de jetons vit dedans, et le laisser dans le seul bloc
          des images aurait caché le coût d'un document joint seul. */}
      {attachments !== undefined && (attachments.items.length > 0 || attachments.documents.length > 0) && (
        <div className={isLarge ? "flex flex-wrap items-center gap-3 px-5 pt-4" : "flex flex-wrap items-center gap-2 px-4 pt-3"}>
          {attachments.items.map((a, i) => (
            <div key={a.original.slice(-24) + i} className="relative">
              {/* UN BOUTON, pas une image cliquable : il est atteignable au clavier, il
                  s'annonce, et son nom dit ce qu'il fait. Une `<img onClick>` serait
                  invisible à qui n'a pas de souris. */}
              <button
                type="button"
                onClick={() => setADessiner(i)}
                disabled={sending}
                title="Draw on this image"
                aria-label={`Draw on attachment ${i + 1}`}
                className="block rounded-md transition hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <img
                  src={a.url}
                  alt={`Attachment ${i + 1}, ${a.width} by ${a.height} pixels${a.strokes.length > 0 ? ", annotated" : ""}`}
                  className={isLarge ? "h-16 w-16 rounded-lg border border-border object-cover" : "h-10 w-10 rounded-md border border-border object-cover"}
                />
              </button>
              <button
                type="button"
                onClick={() => attachments.remove(i)}
                disabled={sending}
                aria-label={`Remove attachment ${i + 1}`}
                title="Remove"
                className="absolute -right-1.5 -top-1.5 rounded-full border border-border bg-surface px-1.5 text-[10px] leading-4 text-text-secondary shadow-[var(--shadow-raised-sm)] transition hover:text-text disabled:opacity-50"
              >
                ×
              </button>
            </div>
          ))}
          {/* UN DOCUMENT N'A PAS DE VIGNETTE : c'est son NOM qui dit ce qu'on envoie. Son coût en
              jetons n'est plus affiché (2026-10-18) : le client compte en dollars de crédit, et
              ne voit que son solde. On ne montre pas un extrait du texte — il
              faudrait choisir lequel, et un extrait mal choisi fait croire qu'on n'a lu que
              ça. Pas de clic non plus : on ne dessine pas sur du texte. */}
          {attachments.documents.map((d, i) => (
            <div
              key={d.name + i}
              className={isLarge
                ? "flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2"
                : "flex items-center gap-1.5 rounded-md border border-border bg-surface px-2 py-1"}
            >
              <DocIcon size={size} />
              <span className={isLarge ? "max-w-[14rem] truncate text-xs" : "max-w-[8rem] truncate text-[10px]"} title={d.name}>
                {d.name}
              </span>
              <button
                type="button"
                onClick={() => attachments.removeDocument(i)}
                disabled={sending}
                aria-label={`Remove document ${d.name}`}
                title="Remove"
                className="rounded-full px-1 text-[11px] leading-4 text-text-secondary transition hover:text-text disabled:opacity-50"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      {attachments?.note != null && (
        <p
          aria-live="polite"
          className={isLarge ? "px-5 pt-3 text-sm text-danger" : "px-4 pt-2 text-[11px] text-danger"}
        >
          {attachments.note}
        </p>
      )}
      <textarea
        value={text}
        onChange={(e) => onTextChange(e.target.value)}
        onKeyDown={onKey}
        // COLLER UNE IMAGE. Demande de l'opérateur le 2026-10-04 : « i want to be able to
        // paste an image ». C'est le geste naturel après une capture d'écran — le trombone
        // oblige à l'enregistrer d'abord, à la retrouver, à la choisir.
        //
        // AUCUNE RÈGLE ICI. `filesFromPaste` décide s'il faut prendre le collage (le texte
        // gagne toujours), et `add` juge les fichiers — type, nombre, lisibilité — exactement
        // comme pour le trombone. Même plafond, même coût, un seul juge.
        //
        // `preventDefault` SEULEMENT quand on prend : sinon on volerait un collage de texte.
        onPaste={(e) => {
          if (attachments === undefined) return;
          const presse = e.clipboardData;
          const fichiers = filesFromPaste(
            presse ? { files: presse.files, text: presse.getData("text/plain") } : null,
          );
          if (fichiers === null) return;
          e.preventDefault();
          void attachments.add(fichiers);
        }}
        disabled={sending && !queuing}
        rows={isLarge ? 15 : 3}
        placeholder={askPending
          ? "Your answer, in your own words…"
          : queuing
            ? "Write while it works — sent when this run ends…"
          : mode === "chat"
            ? "Ask a question about your app — no changes…"
            : "Describe what to build or change…"}
        // pb-10 leaves room for the bottom toolbar (mode pill + actions).
        // Modal gets a comfortable reading-size font (text-base = 16px);
        // inline stays compact since the chat column is narrow.
        className={
          isLarge
            ? "w-full resize-none rounded-2xl bg-transparent px-5 pb-12 pt-4 text-base leading-relaxed outline-none placeholder:text-text-secondary"
            : "w-full resize-none rounded-2xl bg-transparent px-4 pb-10 pt-3 text-xs  leading-relaxed outline-none placeholder:text-text-secondary"
        }
      />
      {/* CE QUI EST EN TRAIN D'ÊTRE DIT, ou pourquoi ça s'est arrêté.
          Le provisoire est en gris et en italique : il n'est PAS encore dans la boîte, et
          l'utilisateur doit le voir comme tel. Voir ses mots arriver est ce qui lui dit qu'on
          l'écoute — sans ça, un micro allumé est indistinguable d'un micro cassé.
          Posé dans le rembourrage bas que la boîte réserve déjà à la barre d'outils, donc il
          ne recouvre pas le texte ; `truncate` garde une ligne quoi qu'il arrive. */}
      {(dictation.interim !== "" || dictation.note !== null) && (
        <div
          aria-live="polite"
          className={isLarge
            ? "pointer-events-none absolute inset-x-0 bottom-12 px-5 text-sm"
            : "pointer-events-none absolute inset-x-0 bottom-10 px-4 text-[11px]"}
        >
          <p className={dictation.note !== null ? "truncate text-danger" : "truncate italic text-text-secondary"}>
            {dictation.note ?? dictation.interim}
          </p>
        </div>
      )}
      {/* Toolbar — larger padding + gap in the modal so the scaled-up
          controls have room to breathe. */}
      <div className={
        isLarge
          ? "pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-between px-4 pb-4"
          : "pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-between px-2.5 pb-2.5"
      }>
        <div className="pointer-events-auto">
          {onModeChange && <ModePill size={size} mode={mode} onChange={onModeChange} />}
        </div>
        <div className={isLarge ? "pointer-events-auto flex items-center gap-3" : "pointer-events-auto flex items-center gap-2"}>
          {/* LE TROMBONE N'EXISTE QUE SI JOINDRE EST PERMIS — l'état est absent sinon, donc
              il n'y a aucune branche où il pourrait apparaître par erreur. L'entrée de
              fichier est cachée et pilotée par le bouton : un `<input type=file>` nu ne se
              style pas, et c'est le bouton qui porte l'état désactivé. */}
          {attachments !== undefined && onSend && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept={[...ATTACH_TYPES, ...DOC_TYPES].join(",")}
                multiple
                hidden
                onChange={(e) => {
                  const f = e.target.files;
                  if (f && f.length > 0) void attachments.add(f);
                  // Remis à zéro : sans ça, rechoisir LE MÊME fichier ne déclenche aucun
                  // événement et le bouton a l'air cassé.
                  e.target.value = "";
                }}
              />
              <IconButton
                size={size}
                onClick={() => fileRef.current?.click()}
                // L'INFOBULLE DIT LE COLLAGE, parce que personne ne le devine. Le nom
                // accessible reste court : il est lu à chaque tabulation, et « or paste one »
                // n'est pas une action que ce bouton effectue.
                title="Attach an image or a document (PDF, Word, text) — or paste an image into the box"
                ariaLabel="Attach an image or a document"
              >
                <ClipIcon size={size} />
              </IconButton>
            </>
          )}
          {/* LE MICRO N'EXISTE QUE SI LE NAVIGATEUR SAIT ÉCOUTER. Un bouton qui ne fait rien
              est pire que pas de bouton : l'utilisateur croit que c'est cassé chez lui.
              Désactivé pendant un envoi, comme la boîte elle-même. */}
          {dictation.supported && onSend && (
            <button
              type="button"
              onClick={dictation.toggle}
              disabled={sending}
              title={dictation.listening ? "Stop dictation" : "Dictate instead of typing"}
              aria-label={dictation.listening ? "Stop dictation" : "Dictate instead of typing"}
              aria-pressed={dictation.listening}
              className={[
                isLarge ? "rounded-full border p-2.5 shadow-[var(--shadow-raised)] transition" : "rounded-full border p-1.5 shadow-[var(--shadow-raised-sm)] transition",
                dictation.listening
                  ? "border-danger-border bg-danger-soft text-danger"
                  : "border-border bg-surface/80 text-text-secondary hover:text-text",
                "disabled:cursor-not-allowed disabled:opacity-50",
              ].join(" ")}
            >
              <MicIcon size={size} />
            </button>
          )}
          {!isLarge && onExpand && (
            <IconButton size={size} onClick={onExpand} title="Expand" ariaLabel="Expand composer">
              <ExpandIcon size={size} />
            </IconButton>
          )}
          {isLarge && onCollapse && (
            <IconButton size={size} onClick={onCollapse} title="Collapse" ariaLabel="Collapse composer">
              <CollapseIcon size={size} />
            </IconButton>
          )}
          {showAbort && queuing && (
            <button
              type="button"
              onClick={onSend}
              disabled={!canSend}
              aria-label="Queue message"
              title="Sent as your next message when this run ends — nothing is stopped"
              // AN ARROW, NOT A WORD (operator's choice, 2026-10-24) — the name stays for screen readers
              // in aria-label, and what the arrow does in the tooltip.
              className={
                isLarge
                  ? "rounded-full bg-accent p-2.5 text-white shadow-[0_2px_6px_rgba(0,0,0,0.20)] transition hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
                  : "rounded-full bg-accent p-1.5 text-white shadow-[0_2px_4px_rgba(0,0,0,0.18)] transition hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
              }
            >
              <ArrowUpIcon size={size} />
            </button>
          )}
          {showAbort ? (
            <button
              type="button"
              onClick={onAbort}
              aria-label="Abort run"
              className={
                isLarge
                  ? "rounded-full border border-danger-border bg-danger-soft px-5 py-2.5 text-sm font-semibold text-danger shadow-[var(--shadow-accent)] transition hover:bg-danger-soft"
                  : "rounded-full border border-danger-border bg-danger-soft px-3.5 py-1.5 text-[11px] font-semibold text-danger shadow-[var(--shadow-raised-sm)] transition hover:bg-danger-soft"
              }
            >
              Abort
            </button>
          ) : (
            <button
              type="button"
              onClick={onSend}
              disabled={!canSend}
              aria-label="Send"
              className={
                isLarge
                  ? "rounded-full bg-accent px-5 py-2.5 text-sm font-semibold text-white shadow-[0_2px_6px_rgba(0,0,0,0.20)] transition hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
                  : "rounded-full bg-accent px-3.5 py-1.5 text-[11px] font-semibold text-white shadow-[0_2px_4px_rgba(0,0,0,0.18)] transition hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
              }
            >
              {sending ? "Sending…" : "Send"}
            </button>
          )}
        </div>
      </div>
      </>
      )}
    </div>
  );
}

// Centered modal hosting the large composer. Backdrop dims + blurs the
// page behind it; click to dismiss. The composer itself is the same
// component as inline — same 3D bar — so the visual reads as "the chat
// input got bigger", not "a different window appeared".
function ExpandedComposerModal({
  mode, onModeChange, text, onTextChange, onClose, onSend, sending,
  runActive, onAbort, askPending, onPickOption, attachments, queuing = false,
}: {
  mode:         ChatMode;
  onModeChange: (m: ChatMode) => void;
  text:         string;
  onTextChange: (t: string) => void;
  onClose:      () => void;
  onSend?:      () => void;
  sending?:     boolean;
  runActive?:   boolean;
  onAbort?:     () => void;
  askPending?:  AskPending | null;
  onPickOption?: (index: number) => void;
  /** Absent ⇒ le trombone n'existe pas. C'est la façon dont « pas de multimodal hors du
   *  mode pas-cher » arrive jusqu'au bouton : un état absent, pas un drapeau à tester. */
  attachments?: Attachments;
  queuing?:     boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-6">
      {/* Backdrop — click to dismiss. */}
      <div
        onClick={onClose}
        className="absolute inset-0 bg-black/30 backdrop-blur-sm"
        aria-hidden="true"
      />
      <div className="relative w-full max-w-3xl">
        <ChatComposer
          size="large"
          mode={mode} onModeChange={onModeChange}
          text={text} onTextChange={onTextChange}
          onCollapse={onClose}
          onSend={onSend}
          sending={sending}
          runActive={runActive}
          onAbort={onAbort}
          askPending={askPending}
          onPickOption={onPickOption}
          attachments={attachments}
          queuing={queuing}
        />
      </div>
    </div>
  );
}

// Small square icon button — neutral container that matches the
// composer's surface (subtle border, light face). Used for expand /
// collapse so the action affordance reads as a peer of Send without
// stealing its emphasis.
function IconButton({
  onClick, title, ariaLabel, children, size = "compact",
}: {
  onClick:   () => void;
  title:     string;
  ariaLabel: string;
  children:  React.ReactNode;
  size?:     "compact" | "large";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={ariaLabel}
      className={
        size === "large"
          ? "rounded-full border border-border bg-surface/80 p-2.5 text-text-secondary shadow-[var(--shadow-raised)] transition hover:text-text"
          : "rounded-full border border-border bg-surface/80 p-1.5 text-text-secondary shadow-[var(--shadow-raised-sm)] transition hover:text-text"
      }
    >
      {children}
    </button>
  );
}

// Glyphs accept a `size` so they scale with the wrapping IconButton —
// keeps the visual stroke weight balanced against the bigger target.
// Le trombone. Un dessin, pas un emoji : il doit suivre la couleur du texte et se mettre à
// l'échelle avec les deux tailles du composeur.
function ClipIcon({ size = "compact" }: { size?: "compact" | "large" }) {
  const px = size === "large" ? 18 : 13;
  return (
    <svg viewBox="0 0 24 24" width={px} height={px} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21.44 11.05 12.25 20.24a5 5 0 0 1-7.07-7.07l8.49-8.49a3 3 0 0 1 4.24 4.24l-8.49 8.49a1 1 0 0 1-1.41-1.41l7.78-7.78" />
    </svg>
  );
}

// UNE FEUILLE AVEC UN COIN PLIÉ. Le signe d'un document, pas d'un format : un PDF, un .docx
// et un .txt portent la même icône, parce que l'utilisateur a déjà son NOM à côté — et parce
// qu'une icône par format serait trois dessins à garder d'accord pour la même information.
function DocIcon({ size = "compact" }: { size?: "compact" | "large" }) {
  const px = size === "large" ? 16 : 12;
  return (
    <svg viewBox="0 0 24 24" width={px} height={px} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0 text-text-secondary">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  );
}

// Le micro. Un seul dessin pour les deux états : c'est la COULEUR du bouton qui dit si on
// écoute, pas une deuxième icône — deux dessins à garder d'accord finissent par diverger.
function MicIcon({ size = "compact" }: { size?: "compact" | "large" }) {
  const px = size === "large" ? 18 : 13;
  return (
    <svg viewBox="0 0 24 24" width={px} height={px} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <line x1="12" y1="18" x2="12" y2="22" />
    </svg>
  );
}

function ArrowUpIcon({ size = "compact" }: { size?: "compact" | "large" }) {
  const px = size === "large" ? 18 : 13;
  return (
    <svg viewBox="0 0 24 24" width={px} height={px} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="12" y1="19" x2="12" y2="5" />
      <polyline points="5 12 12 5 19 12" />
    </svg>
  );
}

function ExpandIcon({ size = "compact" }: { size?: "compact" | "large" }) {
  const px = size === "large" ? 18 : 13;
  return (
    <svg viewBox="0 0 24 24" width={px} height={px} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="15 3 21 3 21 9" />
      <polyline points="9 21 3 21 3 15" />
      <line x1="21" y1="3"  x2="14" y2="10" />
      <line x1="3"  y1="21" x2="10" y2="14" />
    </svg>
  );
}

function CollapseIcon({ size = "compact" }: { size?: "compact" | "large" }) {
  const px = size === "large" ? 18 : 13;
  return (
    <svg viewBox="0 0 24 24" width={px} height={px} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="4 14 10 14 10 20" />
      <polyline points="20 10 14 10 14 4" />
      <line x1="14" y1="10" x2="21" y2="3"  />
      <line x1="3"  y1="21" x2="10" y2="14" />
    </svg>
  );
}

// Segmented mode pill — the explicit Chat | Act toggle inside the composer bar.
// Chat = ask a question (read-only, grounded answer); Act = make a change (the
// server auto-picks build vs edit). Inset track + raised active thumb, matching
// the 3D language of the bar.
function ModePill({
  mode, onChange, size = "compact",
}: {
  mode: ChatMode;
  onChange: (m: ChatMode) => void;
  size?: "compact" | "large";
}) {
  const isLarge = size === "large";
  const seg = (id: ChatMode, label: string) => {
    const active = mode === id;
    const base = isLarge
      ? "rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wider transition"
      // `min-h-6` = 24 px : MESURÉ le 2026-10-04 à 23 px de haut, donc sous le minimum de
      // WCAG 2.5.8 (« at least 24 by 24 CSS pixels »), et à TOUTES les largeurs — ce n'était
      // pas un défaut de petit écran. Deux px de padding de plus auraient suffi, mais une
      // hauteur minimale tient la promesse même si la taille du texte change un jour.
      : "flex min-h-6 items-center rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider transition";
    const active_cls = "bg-surface text-text shadow-[var(--shadow-raised)]";
    const idle_cls   = "text-text-secondary hover:text-text";
    return (
      <button
        type="button"
        onClick={() => onChange(id)}
        aria-pressed={active}
        className={`${base} ${active ? active_cls : idle_cls}`}
      >
        {label}
      </button>
    );
  };
  return (
    <div className={
      isLarge
        ? "inline-flex items-center gap-0.5 rounded-full border border-border bg-surface-muted/60 p-1 shadow-[inset_0_1px_2px_rgba(0,0,0,0.08)]"
        : "inline-flex items-center gap-0.5 rounded-full border border-border bg-surface-muted/60 p-0.5 shadow-[inset_0_1px_2px_rgba(0,0,0,0.08)]"
    }>
      {seg("act",  "Act")}
      {seg("chat", "Chat")}
    </div>
  );
}

// 3D-look vertical bar. Two stacked 1-pixel rules create the bevel: a
// darker rule on the LEFT edge (the shadow side, since light is read as
// coming from the top-left) and a near-white rule on the RIGHT edge (the
// lit face). A faint center line + a column of short grip stripes in the
// middle give the bar physical thickness, like a slider handle.
//
// Static — not wired to a resize gesture for now. The shape signals
// "draggable divider" enough that we can attach a resize later without
// changing the visual.
/**
 * La séparation entre le fil et l'aperçu — et la POIGNÉE qui règle leur partage.
 *
 * Elle avait déjà l'apparence d'un curseur (les cinq stries du milieu, « reads as the grip
 * texture on a slider handle ») sans en être un : `aria-hidden`, inerte. Elle l'est
 * devenue, à la demande de l'opérateur, qui ne pouvait pas élargir son fil.
 *
 * ── ELLE SE MANŒUVRE AUSSI AU CLAVIER ──────────────────────────────────────
 *
 * `role="separator"` + `aria-orientation="vertical"` est le rôle ARIA de cet objet, et il
 * attend des flèches. Une poignée qu'on ne peut qu'attraper à la souris exclut qui ne
 * tient pas de souris — et ce ne coûte ici que deux touches.
 *
 * Le double-clic rend la disposition d'origine : un réglage qu'on ne peut pas défaire est
 * un piège.
 */
function Divider3D({ onResize, onReset, width }: {
  onResize: (clientX: number, persiste: boolean) => void;
  onReset: () => void;
  width: number | null;
}) {
  // Le pas du clavier. 16 px est assez pour qu'une pression se voie, assez petit pour
  // viser : c'est le rythme d'espacement du reste de la console (gap-4 = 16 px).
  const PAS = 16;
  return (
    <div
      className="relative hidden w-2 self-stretch lg:block lg:cursor-col-resize touch-none select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      role="separator"
      aria-orientation="vertical"
      aria-label="Largeur du fil — flèches pour ajuster, double-clic pour rétablir"
      tabIndex={0}
      onPointerDown={(e) => {
        // La capture suit le pointeur MÊME quand il sort de la poignée (8 px de large) :
        // sans elle, le glissement s'arrête dès que la souris dépasse, ce qui est tout le
        // temps. Le navigateur la rend au relâchement.
        e.currentTarget.setPointerCapture(e.pointerId);
        e.preventDefault();
      }}
      onPointerMove={(e) => {
        if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
        // On n'écrit pas dans le stockage à chaque pixel : seulement au relâchement.
        onResize(e.clientX, false);
      }}
      onPointerUp={(e) => {
        if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
        e.currentTarget.releasePointerCapture(e.pointerId);
        onResize(e.clientX, true);
      }}
      onDoubleClick={onReset}
      onKeyDown={(e) => {
        if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
        e.preventDefault();
        // Au clavier il n'y a pas de pointeur : on part de la largeur courante. Quand
        // l'opérateur n'a jamais réglé (`null`), la première pression part du plancher —
        // la seule valeur que ce composant connaisse sans mesurer le DOM.
        const base = width ?? CHAT_MIN_PX;
        const voulue = base + (e.key === "ArrowRight" ? PAS : -PAS);
        const bord = e.currentTarget.parentElement?.getBoundingClientRect().left ?? 0;
        onResize(bord + clampChatWidth(voulue, window.innerWidth), true);
      }}
    >
      {/* Shadow edge (left) */}
      <div className="absolute inset-y-0 left-0 w-px bg-black/15" />
      {/* Highlight edge (right) */}
      <div className="absolute inset-y-0 right-0 w-px bg-surface/70" />
      {/* Faint center line — adds visual weight without making the bar dark */}
      <div className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-black/5" />
      {/* Grip stripes — a short stack centered vertically. Reads as the
          grip texture on a slider handle. */}
      <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col gap-1">
        <span className="block h-px w-2.5 rounded-full bg-black/20" />
        <span className="block h-px w-2.5 rounded-full bg-black/20" />
        <span className="block h-px w-2.5 rounded-full bg-black/20" />
        <span className="block h-px w-2.5 rounded-full bg-black/20" />
        <span className="block h-px w-2.5 rounded-full bg-black/20" />
      </div>
    </div>
  );
}

// Preview column. Either an iframe of the dev app, or a frosted
// placeholder with the perimetr.platform logo. Edge-to-edge — no inner
// border or rounded corners; the divider on the left + the viewport edge
// on the right are the boundaries.
function PreviewColumn({ ready, url }: { ready: boolean; url: string }) {
  if (ready) {
    return (
      <iframe
        src={url}
        title="App preview"
        className="h-full w-full flex-1 border-0 bg-surface"
        /* `allow-downloads` : voir la note de l'autre aperçu, plus haut. Les DEUX en ont besoin —
           n'en corriger qu'un laisserait la panne vivante sur la moitié des écrans. */
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-modals allow-downloads"
      />
    );
  }
  return (
    <div className="relative h-full w-full flex-1 overflow-hidden">
      {/* Frosted backdrop — soft theme-coloured gradient, then a heavy
          blur. Pointer-events:none so it never eats clicks (defensive;
          nothing here is interactive anyway). */}
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-accent/15 via-surface-muted to-surface-muted backdrop-blur-2xl"
        aria-hidden="true"
      />
      {/* Centered logo. Same mark as the navbar so the operator reads it
          as our own surface, not a broken iframe. */}
      <div className="relative flex h-full items-center justify-center">
        <div className="flex items-center gap-3 text-2xl font-semibold tracking-tight">
          <Logo className="h-11 w-11 shrink-0 text-accent" />
          <span><span className="text-accent">get</span>perimetr</span>
        </div>
      </div>
    </div>
  );
}

// Exit full screen. The ENTER glyph already exists above (`ExpandIcon`, the composer's own expand uses
// the same four corner brackets and the same meaning), so only its mirror is new — brackets pointing
// inward. Reusing the one that exists is not thrift: two drawings of "expand" in one file drift, and
// then the same action looks like two different actions.
function ShrinkIcon() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="14 10 21 10 21 3" /><polyline points="10 14 3 14 3 21" />
      <line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  );
}
